import { Injectable, signal, computed, inject } from '@angular/core';
import { DayTypeType, DeliveryOptions, Festival, Lodge, Package, Product } from '@shared';
import { ApiService } from './api.service';

@Injectable({
  providedIn: 'root',
})
export class ProgressService {
  public readonly progressPercent = computed(() => [this.selectedFestival(), this.selectedDayslot(), this.selectedLodge(), this.selectedPersons(), this.selectedShipping()].filter((val) => !!val).length * 20);
  public readonly selectedDate = signal<string|null>(null);
  public readonly selectedLodge = signal<Lodge|null>(null);
  public readonly selectedDayslot = signal<DayTypeType|null>(null);
  public readonly selectedPackage = signal<Package|null>(null);
  public readonly selectedAdditionalProducts = signal<Product[]>([]);
  public readonly selectedFestival = signal<Festival|null>(null);
  public readonly selectedPersons = signal<number|null>(null);
  public readonly selectedShipping = signal<DeliveryOptions|null>(null);
  private readonly apiService = inject(ApiService)

  public resetAll() {
    this.resetPersons();
    this.resetLodge();
    this.resetPackage();
    this.resetDate();
    this.resetDaySlot();
    this.resetAdditionalProducts();
  }

  public festivalSelected(id: number): void {
    this.resetDate();
    this.selectedFestival.set(null);
    this.apiService.fetchFestivalData(id).subscribe((festival) => {
      this.selectedFestival.set(festival);
    });
  }

  public resetAdditionalProducts() {
    this.selectedAdditionalProducts.set([]);
  }

  public additionalProductsSelected(products: Product[]) {
    this.selectedAdditionalProducts.set(products);
  }

  public shippingSelected(shipping: DeliveryOptions) {
    this.selectedShipping.set(shipping);
  }

  public resetLodge(): void {
    this.resetPackage();
    this.resetAdditionalProducts();
    this.selectedLodge.set(null);
  }

  public resetPackage(): void {
    this.selectedPackage.set(null);
  }

  public lodgeSelected(lodge: Lodge) {
    this.resetLodge();
    this.resetPersons();
    this.selectedLodge.set(lodge);
    this.additionalProductsSelected(lodge.products?.map((product) => {
      const copy = Object.assign({}, product);
      copy.pivot = Object.assign({}, product.pivot);
      copy.pivot.amount = 0;
      return copy;
    }) || []);
  }

  public packageSelected(pkg: Package|null) {
    this.resetPackage();
    if(pkg && !!pkg.id && pkg.id > 0) {
      this.selectedPackage.set(pkg);
    }
  }

  public resetDate(): void {
    this.resetDaySlot();
    this.selectedDate.set(null);
  }

  public resetDaySlot(): void {
    this.selectedDayslot.set(null);
  }

  public resetPersons(): void {
    this.selectedPersons.set(null);
  }

  public dateSelected(date: string): void {
    this.selectedDate.set(date);
    this.resetDaySlot();
  }

  public personsSelected(persons: number): void {
    this.selectedPersons.set(persons);
  }

  public dayTypeSelected(date: string, daytype: DayTypeType) {
    this.selectedDate.set(date);
    this.selectedDayslot.set(daytype);
    this.resetPersons();
  }
}
